import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { Zap, ArrowUp } from "lucide-react";

import { useSession } from "@/contexts/SessionContext";

/**
 * Header token counter — shows the live NXT balance and the ASCEND pill.
 * Both elements click to open the NexaVault drawer.
 */
export default function TokenCounter() {
    const {
        balance,
        openDrawer,
        openSubscriptionDrawer,
        tier,
        trialActive,
        loading,
    } = useSession();
    const [displayed, setDisplayed] = useState(balance);
    const motionVal = useMotionValue(balance);
    const rounded = useTransform(motionVal, (v) => Math.round(v));
    const prevRef = useRef(balance);

    useEffect(() => {
        const prev = prevRef.current;
        prevRef.current = balance;
        if (prev === balance) {
            setDisplayed(balance);
            return;
        }
        // Animate count from prev → balance
        const controls = animate(motionVal, balance, {
            duration: 0.8,
            ease: "easeOut",
        });
        const unsub = rounded.on("change", (v) => setDisplayed(v));
        return () => {
            controls.stop();
            unsub();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [balance]);

    const direction = balance > prevRef.current ? "up" : balance < prevRef.current ? "down" : "same";

    // Phase 4-b: ASCEND routing depends on tier.
    //  - trial / trial-expired → SubscriptionDrawer (trial users can't top up)
    //  - creator / pro         → NexaVault drawer (existing $5 top-up flow)
    const isTrial = tier === "trial" || tier === "expired";
    const trialExpired = tier === "trial" && trialActive === false;
    const handleAscend = () => {
        if (isTrial) {
            openSubscriptionDrawer({
                code: trialExpired ? "trial_expired" : "tier_required",
                required: "creator",
                feature: "Top-ups",
            });
        } else {
            openDrawer("topup");
        }
    };

    return (
        <div className="flex items-center gap-2">
            <button
                type="button"
                onClick={() => openDrawer("balance")}
                className="group inline-flex items-center gap-2 rounded-lg border border-[#1F2A40] bg-[#111726] px-3 py-1.5 text-sm font-medium text-[#E8ECF5] transition-all hover:border-[#00F5D4]/60 hover:shadow-[0_0_18px_rgba(0,245,212,0.18)]"
                data-testid="token-counter-btn"
                aria-label="Open NexaVault"
            >
                <Zap
                    className="h-3.5 w-3.5 text-[#00F5D4]"
                    fill="currentColor"
                    strokeWidth={0}
                />
                <motion.span
                    key={direction}
                    initial={{
                        scale: 1,
                        color: direction === "up" ? "#00F5D4" : direction === "down" ? "#FF5470" : "#E8ECF5",
                    }}
                    animate={{ scale: 1, color: "#E8ECF5" }}
                    transition={{ duration: 0.8 }}
                    className="font-mono tabular-nums tracking-tight"
                    data-testid="token-counter-value"
                >
                    {loading ? "…" : displayed}
                </motion.span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A93A6]">
                    NXT
                </span>
            </button>

            <button
                type="button"
                onClick={handleAscend}
                className="group relative inline-flex items-center gap-1.5 overflow-hidden rounded-full bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-all hover:shadow-[0_0_22px_rgba(0,245,212,0.45)]"
                data-testid="ascend-btn"
                data-ascend-target={isTrial ? "subscription" : "topup"}
            >
                <span className="relative z-10">Ascend</span>
                <ArrowUp className="relative z-10 h-3 w-3 transition-transform duration-200 group-hover:-translate-y-0.5" />
                <span
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-[#00F5D4]/35 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                />
            </button>
        </div>
    );
}
